import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, User, Trash, Key, ShieldCheck, Monitor } from "@phosphor-icons/react";

export default function UsersPage() {
  useDocumentTitle("Utenti");
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "tech" });

  // Trusted devices state
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const { data } = await api.get("/admin/trusted-devices");
      setDevices(data);
    } catch (e) {
      // silent
    } finally {
      setDevicesLoading(false);
    }
  };

  const revokeDevice = async (d) => {
    if (!window.confirm(`Revocare "${d.label}" di ${d.user_email}?`)) return;
    try {
      await api.delete(`/admin/trusted-devices/${d.id}`);
      toast.success("Dispositivo revocato");
      loadDevices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const revokeAllDevices = async () => {
    if (!window.confirm("Revocare TUTTI i dispositivi fidati di TUTTI gli utenti? Al prossimo login sarà richiesta la 2FA su tutti i PC.")) return;
    try {
      await api.delete("/admin/trusted-devices");
      toast.success("Tutti i dispositivi fidati revocati");
      loadDevices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadDevices(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password minima 8 caratteri");
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpenCreate(false);
      setForm({ name: "", email: "", password: "", role: "tech" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Utente disattivato" : "Utente attivato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare definitivamente ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitReset = async () => {
    if (newPass.length < 8) return toast.error("Password minima 8 caratteri");
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: newPass });
      toast.success("Password reimpostata");
      setResetTarget(null);
      setNewPass("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// AMMINISTRAZIONE</div>
          <h1 className="text-4xl font-display font-bold tracking-tight">Utenti</h1>
          <p className="text-muted-foreground mt-2">Gestisci gli operatori del negozio.</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="rounded-full gap-2">
              <Plus size={16} weight="bold" /> Nuovo utente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crea nuovo utente</DialogTitle>
              <DialogDescription>Un nuovo operatore potrà accedere al gestionale.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="new-user-form">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Nome</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Password (min 8)</Label>
                <Input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tech">Tecnico</SelectItem>
                    <SelectItem value="admin">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={creating} data-testid="new-user-submit" className="rounded-full">
                  {creating ? "Creo…" : "Crea utente"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <Card className="border-border/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr className="text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Utente</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ruolo</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Attivo</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Caricamento…</td></tr>}
              {!loading && users.map((u) => (
                <tr key={u.id} className="border-t border-border/50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center">
                        <User size={16} weight="duotone" />
                      </div>
                      <div>
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === me?.id}>
                      <SelectTrigger className="w-36" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tech">Tecnico</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={u.id === me?.id} data-testid={`toggle-active-${u.email}`} />
                      {u.active ? (
                        <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15 font-mono text-[10px] uppercase">Attivo</Badge>
                      ) : (
                        <Badge className="bg-muted text-muted-foreground border border-border font-mono text-[10px] uppercase">Disattivato</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setResetTarget(u)} data-testid={`reset-pw-${u.email}`} className="gap-1">
                        <Key size={14} /> Reset password
                      </Button>
                      {u.id !== me?.id && (
                        <Button size="sm" variant="ghost" onClick={() => remove(u)} className="text-destructive hover:text-destructive" data-testid={`delete-user-${u.email}`}>
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Trusted Devices (admin overview) */}
      <Card className="border-border/70 rounded-lg mt-8" data-testid="admin-trusted-devices-card">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-secondary/40">
          <div className="flex items-center gap-2">
            <Monitor size={18} weight="duotone" className="text-primary" />
            <div>
              <div className="font-display font-bold tracking-tight">Dispositivi fidati 2FA</div>
              <div className="text-xs text-muted-foreground">Elenco dei dispositivi che possono saltare il codice 2FA per 90 giorni.</div>
            </div>
          </div>
          {devices.length > 0 && (
            <Button variant="outline" size="sm" onClick={revokeAllDevices} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive gap-1" data-testid="admin-revoke-all-devices-btn">
              <Trash size={14} /> Revoca tutti
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Utente</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Dispositivo</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Aggiunto</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Scade</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {devicesLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Caricamento…</td></tr>}
              {!devicesLoading && devices.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground font-mono">— Nessun dispositivo fidato attivo —</td></tr>
              )}
              {!devicesLoading && devices.map((d) => (
                <tr key={d.id} className="border-t border-border/50" data-testid={`admin-device-row-${d.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{d.user_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{d.user_email}</div>
                  </td>
                  <td className="px-4 py-2.5">{d.label}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{d.created_at ? new Date(d.created_at).toLocaleString("it-IT") : "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{d.expires_at ? new Date(d.expires_at).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end">
                      <Button variant="ghost" size="sm" onClick={() => revokeDevice(d)} className="text-destructive hover:text-destructive rounded-full" data-testid={`admin-revoke-device-${d.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPass(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Imposta una nuova password per {resetTarget?.email}</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Nuova password (min 8)</Label>
            <Input type="password" minLength={8} value={newPass} onChange={(e) => setNewPass(e.target.value)} className="mt-1.5" data-testid="reset-pw-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setNewPass(""); }}>Annulla</Button>
            <Button onClick={submitReset} data-testid="reset-pw-submit" className="gap-2"><ShieldCheck size={16} /> Reimposta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
